import { SocketSessionRetry } from '../../components/SocketSessionRetry';
/**
 * @page FloorManagerPage
 * @description 場管操作台 — 全場總覽（場管 PWA，橫式）
 * Route: /floor/:tournamentId
 * UI Reference: UI-6-2
 */

import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatTime } from '../../lib/format';
import { useTranslation } from 'react-i18next';
import { useTournamentSocket } from '../../hooks/useTournamentSocket';
import { useAuthStore } from '../../store/auth.store';
import { timeoutSignal } from '../../lib/timeoutSignal';

const API = (import.meta as unknown as { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? '';

function authHeaders(): HeadersInit {
  const token = useAuthStore.getState().accessToken ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function jsonOrThrow(r: Response) {
  if (!r.ok) throw new Error(`API error: ${r.status}`);
  const response = await r.json();
  return response.data;
}

interface SeatAssignment {
  id: string;
  entryId: string;
  seatNo: number;
  entry?: { nickname?: string; memberNo?: string };
}

interface TableWithSeats {
  tableId: string;
  isFinalTable: boolean;
  table: { id: string; tableNo: number; name?: string; maxSeats: number };
  seats: SeatAssignment[];
}

interface Tournament {
  id: string;
  name: string;
  status: string;
  activePlayers: number;
  totalEntries: number;
}

interface TransferModalProps {
  fromTableId: string;
  fromSeatNo: number;
  fromEntryId: string;
  tables: TableWithSeats[];
  tournamentId: string;
  onClose: () => void;
  onTransfer: (toTableId: string, toSeatNo: number) => void;
}

function TransferModal({
  fromTableId,
  fromSeatNo,
  tables,
  onClose,
  onTransfer,
}: TransferModalProps) {
  const { t } = useTranslation('tournament');
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedSeat, setSelectedSeat] = useState<number | ''>('');

  const targetTable = tables.find((t) => t.tableId === selectedTable);
  const occupiedSeats = new Set(targetTable?.seats.map((s) => s.seatNo) ?? []);
  const availableSeats = targetTable
    ? Array.from({ length: targetTable.table.maxSeats }, (_, i) => i + 1).filter(
        (s) => !occupiedSeats.has(s),
      )
    : [];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-8 w-full max-w-md shadow-2xl">
        <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">swap_horiz</span>
          {t('floor.tableTransfer')}
        </h3>
        <p className="text-slate-400 text-sm mb-6">
          {t('floor.transferFrom', { table: fromTableId.slice(0, 8), seat: fromSeatNo })}
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">
              {t('floor.targetTableLabel')}
            </label>
            <select
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm"
              value={selectedTable}
              onChange={(e) => {
                setSelectedTable(e.target.value);
                setSelectedSeat('');
              }}
            >
              <option value="">{t('floor.selectTableTarget')}</option>
              {tables
                .filter((tb) => tb.tableId !== fromTableId)
                .map((tb) => (
                  <option key={tb.tableId} value={tb.tableId}>
                    {t('floor.tableWithPlayers', {
                      no: tb.table.tableNo,
                      filled: tb.seats.length,
                      max: tb.table.maxSeats,
                    })}
                  </option>
                ))}
            </select>
          </div>
          {selectedTable && (
            <div>
              <label className="block text-sm font-semibold mb-2">{t('floor.targetSeat')}</label>
              <div className="grid grid-cols-5 gap-2">
                {availableSeats.map((seat) => (
                  <button
                    key={seat}
                    onClick={() => setSelectedSeat(seat)}
                    className={`min-h-[44px] rounded-lg font-bold text-sm transition-colors ${
                      selectedSeat === seat
                        ? 'bg-primary text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {seat}
                  </button>
                ))}
                {availableSeats.length === 0 && (
                  <p className="col-span-5 text-slate-500 text-sm text-center py-2">
                    {t('floor.noEmptySeats')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            className="flex-1 min-h-[44px] rounded-xl border border-slate-700 text-slate-300 font-bold"
          >
            {t('cancel')}
          </button>
          <button
            disabled={!selectedTable || !selectedSeat}
            onClick={() => onTransfer(selectedTable, Number(selectedSeat))}
            className="flex-1 min-h-[44px] rounded-xl bg-primary text-white font-bold disabled:opacity-40"
          >
            {t('floor.confirmTransfer')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FloorManagerPage() {
  const { t } = useTranslation('tournament');
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.accessToken) ?? '';

  const [transferTarget, setTransferTarget] = useState<{
    fromTableId: string;
    fromSeatNo: number;
    fromEntryId: string;
  } | null>(null);

  const { sessionPaused, retrySocket, timerUpdate, sendTimerAction, connected, socketError } =
    useTournamentSocket(tournamentId ?? '', token, !!tournamentId);

  const remainingSeconds = timerUpdate?.timerState?.remainingSeconds ?? 0;
  const isRunning = timerUpdate?.timerState?.isRunning ?? false;
  const level = timerUpdate?.timerState?.level ?? 1;

  // Local countdown
  const [countdown, setCountdown] = useState(remainingSeconds);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setCountdown(remainingSeconds);
  }, [remainingSeconds]);

  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (!isRunning) return;
    countdownRef.current = setInterval(() => setCountdown((p) => Math.max(0, p - 1)), 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isRunning, remainingSeconds]);

  const { data: tournament } = useQuery<Tournament>({
    queryKey: ['tournament', tournamentId],
    queryFn: () =>
      fetch(`${API}/api/tournament/${tournamentId}`, {
        headers: authHeaders(),
        signal: timeoutSignal(10000),
      }).then(jsonOrThrow),
    enabled: !!tournamentId,
    refetchInterval: 30_000,
  });

  const { data: layout = [] } = useQuery<TableWithSeats[]>({
    queryKey: ['tournament-layout', tournamentId],
    queryFn: () =>
      fetch(`${API}/api/venue/tournaments/${tournamentId}/layout`, {
        headers: authHeaders(),
        signal: timeoutSignal(10000),
      }).then(jsonOrThrow),
    enabled: !!tournamentId,
    refetchInterval: 15_000,
  });

  const transferMutation = useMutation({
    mutationFn: (body: object) =>
      fetch(`${API}/api/venue/tournaments/${tournamentId}/transfers`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
        signal: timeoutSignal(10000),
      }).then(jsonOrThrow),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tournament-layout', tournamentId] });
      setTransferTarget(null);
    },
    onError: (err) => {
      console.warn('[FloorManager] transfer failed:', (err as Error).message);
    },
  });

  // Table balance warning: biggest difference > 2
  const playerCounts = layout.map((t) => t.seats.length);
  const maxPlayers = Math.max(...playerCounts, 0);
  const minPlayers = playerCounts.length > 0 ? Math.min(...playerCounts) : 0;
  const balanceWarning = layout.length >= 2 && maxPlayers - minPlayers >= 2;
  const bigTable = layout.find((t) => t.seats.length === maxPlayers);
  const smallTable = layout.find(
    (t) => t.seats.length === minPlayers && t.tableId !== bigTable?.tableId,
  );

  return (
    <div className="bg-[#101622] text-slate-100 h-screen w-screen flex flex-col overflow-hidden">
      <SocketSessionRetry paused={sessionPaused} retry={retrySocket} />
      {socketError && (
        <div role="alert" className="shrink-0 bg-red-950 px-4 py-3 text-sm text-red-100">
          {socketError}
        </div>
      )}
      {/* Header */}
      <header className="flex items-center justify-between border-b border-slate-800 bg-[#101622] px-6 py-3 z-10 shrink-0">
        <div className="flex items-center gap-4">
          <span className="material-symbols-outlined text-primary text-3xl">token</span>
          <h1 className="text-lg font-bold">
            {t('floor.titleWithName', { name: tournament?.name ?? t('floor.loadingName') })}
          </h1>
          {!connected && (
            <span className="text-xs text-amber-400 bg-amber-900/30 px-2 py-0.5 rounded">
              {t('floor.wsConnecting')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {/* Timer display */}
          <div className="bg-primary px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg shadow-primary/20">
            <span className="material-symbols-outlined text-white">schedule</span>
            <span
              className="text-white font-bold text-xl tracking-wider"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              L{level} {formatTime(countdown)}
            </span>
          </div>
          {/* Timer controls */}
          <div className="flex items-center bg-slate-800 rounded-lg p-1 border border-slate-700">
            {isRunning ? (
              <button
                onClick={() => sendTimerAction({ action: 'pause' })}
                className="w-11 h-11 flex items-center justify-center rounded-md hover:bg-slate-700 transition-colors"
              >
                <span className="material-symbols-outlined">pause</span>
              </button>
            ) : (
              <button
                onClick={() => sendTimerAction({ action: 'start' })}
                className="w-11 h-11 flex items-center justify-center rounded-md hover:bg-slate-700 transition-colors"
              >
                <span className="material-symbols-outlined">play_arrow</span>
              </button>
            )}
            <div className="w-px h-6 bg-slate-700 mx-1" />
            <button
              onClick={() => sendTimerAction({ action: 'next_level' })}
              className="w-11 h-11 flex items-center justify-center rounded-md hover:bg-slate-700 transition-colors"
            >
              <span className="material-symbols-outlined">keyboard_arrow_up</span>
            </button>
            <button
              onClick={() => sendTimerAction({ action: 'prev_level' })}
              className="w-11 h-11 flex items-center justify-center rounded-md hover:bg-slate-700 transition-colors"
            >
              <span className="material-symbols-outlined">keyboard_arrow_down</span>
            </button>
          </div>
        </div>
      </header>

      {/* Balance warning */}
      {balanceWarning && (
        <div className="bg-amber-900/30 border-b border-amber-800/50 px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 text-amber-200">
            <span className="material-symbols-outlined">warning</span>
            <p className="font-medium">
              {t('floor.balanceSuggestion', {
                big: bigTable?.table.tableNo,
                bigCount: maxPlayers,
                small: smallTable?.table.tableNo,
                smallCount: minPlayers,
              })}
            </p>
          </div>
        </div>
      )}

      {/* Table cards — horizontal scroll */}
      <main className="flex-1 overflow-x-auto overflow-y-hidden flex items-stretch p-6 gap-6">
        {layout.map((tableData) => {
          const filledSeats = tableData.seats.length;
          const maxSeats = tableData.table.maxSeats;
          const isCrowded = filledSeats === maxSeats;
          const isEmpty = filledSeats <= Math.floor(maxSeats * 0.4);

          return (
            <div
              key={tableData.tableId}
              className={`flex-none w-[380px] bg-slate-900 rounded-xl border flex flex-col shadow-xl ${
                isEmpty ? 'ring-2 ring-primary/20 border-slate-700' : 'border-slate-800'
              }`}
            >
              {/* Card header */}
              <div className="p-5 border-b border-slate-800 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">
                    {t('venue:tableLabel', { no: tableData.table.tableNo })}
                    {tableData.isFinalTable && (
                      <span className="ml-2 text-xs bg-primary text-white px-2 py-0.5 rounded-full font-bold">
                        {t('floor.finalTableBadge')}
                      </span>
                    )}
                  </h3>
                  <span className="text-sm text-slate-400">
                    {tableData.table.name ?? t('floor.nSeatsTable', { n: maxSeats })}
                  </span>
                </div>
                <div
                  className={`px-3 py-1 rounded-full text-sm font-bold ${
                    isCrowded
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : isEmpty
                        ? 'bg-amber-500/10 text-amber-500'
                        : 'bg-primary/10 text-primary'
                  }`}
                >
                  {t('floor.playersCount', { filled: filledSeats, max: maxSeats })}
                </div>
              </div>

              {/* Seat grid */}
              <div className="flex-1 p-5 overflow-y-auto">
                <div className="grid grid-cols-2 gap-3">
                  {tableData.seats.map((seat) => (
                    <div
                      key={seat.id}
                      className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/50 border border-slate-700 cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() =>
                        setTransferTarget({
                          fromTableId: tableData.tableId,
                          fromSeatNo: seat.seatNo,
                          fromEntryId: seat.entryId,
                        })
                      }
                    >
                      <span className="text-xs text-slate-500 font-mono w-4">{seat.seatNo}</span>
                      <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                      <span className="font-medium truncate text-sm">
                        {seat.entry?.nickname ?? seat.entryId.slice(0, 8)}
                      </span>
                    </div>
                  ))}
                  {/* Empty slots */}
                  {Array.from({ length: Math.max(0, maxSeats - filledSeats) }).map((_, i) => (
                    <div
                      key={`empty-${i}`}
                      className="flex items-center justify-center p-3 rounded-lg border border-dashed border-slate-700 opacity-40 min-h-[46px]"
                    >
                      <span className="text-xs text-slate-500">{t('display:emptySlot')}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 bg-slate-800/30 flex gap-2 border-t border-slate-800">
                <a
                  href={`/dealer/${tournamentId}/${tableData.tableId}`}
                  className="flex-1 min-h-[44px] bg-primary text-white rounded-lg font-bold flex items-center justify-center gap-2 text-sm"
                >
                  <span className="material-symbols-outlined text-lg">login</span>
                  {t('floor.enter')}
                </a>
                <button
                  className="flex-1 min-h-[44px] bg-slate-800 text-slate-300 rounded-lg font-bold flex items-center justify-center gap-2 text-sm hover:bg-slate-700 transition-colors"
                  onClick={() => {
                    const seat = tableData.seats[0];
                    if (seat) {
                      setTransferTarget({
                        fromTableId: tableData.tableId,
                        fromSeatNo: seat.seatNo,
                        fromEntryId: seat.entryId,
                      });
                    }
                  }}
                >
                  <span className="material-symbols-outlined text-lg">swap_horiz</span>
                  {t('floor.transfer')}
                </button>
              </div>
            </div>
          );
        })}

        {/* Add table placeholder */}
        {layout.length === 0 && (
          <div className="flex items-center justify-center w-full text-slate-600">
            <div className="text-center">
              <span className="material-symbols-outlined text-6xl block mb-3">
                table_restaurant
              </span>
              <p>{t('floor.noTablesAssigned')}</p>
            </div>
          </div>
        )}
      </main>

      {/* Footer stats */}
      <footer className="border-t border-slate-800 px-8 py-3 flex items-center gap-12 shrink-0">
        <div className="flex items-center gap-2 text-slate-400">
          <span className="material-symbols-outlined text-primary">groups</span>
          <span className="font-bold text-white">{tournament?.activePlayers ?? 0}</span>
          <span>{t('floor.alive')}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-400">
          <span className="material-symbols-outlined text-primary">table_restaurant</span>
          <span className="font-bold text-white">{layout.length}</span>
          <span>{t('floor.tablesCount')}</span>
        </div>
        <div className="ml-auto text-slate-500 text-sm">
          {new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </footer>

      {/* Transfer Modal */}
      {transferTarget && (
        <TransferModal
          {...transferTarget}
          tables={layout}
          tournamentId={tournamentId ?? ''}
          onClose={() => setTransferTarget(null)}
          onTransfer={(toTableId, toSeatNo) => {
            transferMutation.mutate({
              entryId: transferTarget.fromEntryId,
              fromTableId: transferTarget.fromTableId,
              fromSeatNo: transferTarget.fromSeatNo,
              toTableId,
              toSeatNo,
            });
          }}
        />
      )}
    </div>
  );
}
